const CART_REC_SLIDER = {
  section: ".shopify-section.Cart-Recommendations-Slider, .Cart-Recommendations-Slider",
  sticky: ".mini-cart__rec-slider-sticky",
  stickyToggle: ".mini-cart__rec-slider-sticky__toggle",
  stickyPlus: ".mini-cart__rec-slider-sticky__plus",
  stickyLoader: ".mini-cart__rec-slider-sticky__loader",
  accordionPanel: "collapsible-content.mini-cart__rec-slider-sticky__panel",
  swiper: ".mini-cart__recommendations-slider-list.swiper",
  wrapper: ".swiper-wrapper",
  slide: ".swiper-slide",
  quickBuyHost: "quick-buy-drawer, quick-buy-popover, quick-view-modal",
  leftoverHosts: ":scope > .cart-recommendations-quick-buy-container",
  cartItems: ".Cart-Items",
  imageWrapper: "product-item .product-item__image-wrapper",
  atcLoader: ".product-item__atc-loader"
};

const SWIPER_SYNC_DELAY_MS = 520;
const ACCORDION_CLOSE_SUPPRESS_MS = 450;

const SWIPER_PARAMS = {
  slidesPerView: 1.275,
  spaceBetween: 8,
  threshold: 10,
  preventClicks: false,
  preventClicksPropagation: false,
  touchStartPreventDefault: false,
  noSwipingSelector: "button, a",
  observer: true,
  observeParents: true,
  watchOverflow: true,
  breakpoints: {
    0: { spaceBetween: 8, slidesPerView: 1 },
    375: { spaceBetween: 8, slidesPerView: 1.275 }
  }
};

const ATC_LOADER_TEMPLATE = document.createElement("template");
ATC_LOADER_TEMPLATE.innerHTML = `
    <span hidden class="product-item__atc-loader" aria-hidden="true">
        <span hidden class="product-item__atc-loader-spinner spinner">
            <svg focusable="false" width="16" height="16" class="icon icon--spinner" viewBox="25 25 50 50">
                <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" stroke-width="6"></circle>
            </svg>
        </span>
        <span hidden class="product-item__atc-loader-mark">
            <svg focusable="false" width="20" height="20" class="icon icon--check" viewBox="0 0 32 32">
                <path d="M24.59 8L12.9885 20.6731L7.31806 15.1819L6 16.6956L12.3755 22.8792L13.0805 23.5556L13.7395 22.8309L26 9.43318L24.59 8Z" stroke="currentColor"></path>
            </svg>
        </span>
    </span>
`;

const defineOnce = (name, ctor) => {
  if (!customElements.get(name)) {
    customElements.define(name, ctor);
  }
};

const listen = (target, type, handler, options) => {
  target.addEventListener(type, handler, options);
};

class CartRecommendationsSliderInner extends HTMLElement {
  static cache = Object.create(null);
  static restoreAccordion = false;

  #generation = 0;
  #relocatedHostIds = new Set();
  #abortController = null;
  #accordionAbortController = null;
  #atcObservers = new Set();
  #atcState = new WeakMap();
  #suppressAccordionCloseUntil = 0;
  #pointerInCartItems = false;

  connectedCallback() {
    const generation = ++this.#generation;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    listen(document.documentElement, "Cart:Refresh", this.#onCartRefresh, { signal });
    listen(this, "submit", this.#onProductSubmit, { capture: true, signal });
    listen(this, "Variant:Added", this.#onVariantAdded, { signal });
    this.#restoreAccordionState();
    this.#bindAccordionClose();
    this.#loadRecommendations(generation);
  }

  disconnectedCallback() {
    this.#generation += 1;
    this.#storeAccordionState();
    this.#unbindAccordionClose();
    this.#clearAtcObservers();
    this.#removeRelocatedHosts();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  get productId() {
    return this.getAttribute("product-id");
  }

  get sectionId() {
    return this.getAttribute("section-id");
  }

  get #sectionRoot() {
    return this.closest(CART_REC_SLIDER.section);
  }

  get #accordionPanel() {
    return this.querySelector(CART_REC_SLIDER.accordionPanel);
  }

  #isCurrent(generation) {
    return generation === this.#generation && this.isConnected;
  }

  #onCartRefresh = (event) => {
    CartRecommendationsSliderInner.cache = Object.create(null);
    this.#suppressAccordionCloseUntil = performance.now() + ACCORDION_CLOSE_SUPPRESS_MS;

    const cart = event.detail?.cart;
    const firstProductId = cart?.items?.[0]?.product_id;

    if (firstProductId) {
      this.setAttribute("product-id", String(firstProductId));
    }

    if (Number(cart?.item_count) === 0 || !this.querySelector(CART_REC_SLIDER.sticky)) {
      return;
    }

    this.#setStickyLoading(true);
    this.#loadRecommendations(++this.#generation);
  };

  #toggleSection(visible) {
    const sectionRoot = this.#sectionRoot;

    if (sectionRoot) {
      sectionRoot.hidden = !visible;
    }

    this.hidden = !visible;
  }

  #hideSection() {
    this.#toggleSection(false);
    this.#clearAtcObservers();
    this.#removeRelocatedHosts();
    this.innerHTML = "";
  }

  async #loadRecommendations(generation) {
    const productId = this.productId;
    const sectionId = this.sectionId;
    const cache = CartRecommendationsSliderInner.cache;
    const recommendationsUrl = window.themeVariables?.routes?.productRecommendationsUrl;

    try {
      this.#toggleSection(true);

      if (!productId || !sectionId || !recommendationsUrl) {
        this.#hideSection();
        return;
      }

      if (!cache[productId]) {
        cache[productId] = fetch(`${recommendationsUrl}?product_id=${productId}&limit=10&section_id=${sectionId}`);
      }

      const response = await cache[productId];

      if (!this.#isCurrent(generation)) {
        return;
      }

      if (!response.ok) {
        delete cache[productId];
        this.#hideSection();
        return;
      }

      const html = await response.clone().text();

      if (!this.#isCurrent(generation)) {
        return;
      }

      const sandbox = document.createElement("div");
      sandbox.innerHTML = html;

      const nextRoot = sandbox.querySelector("cart-recommendations-slider-inner");
      const hasSlides = Boolean(nextRoot?.querySelector(CART_REC_SLIDER.slide));

      if (!nextRoot || !hasSlides) {
        delete cache[productId];
        this.#hideSection();
        return;
      }

      this.#applyRecommendations(nextRoot);
    } catch (error) {
      console.warn("cart-recommendations-slider-inner: recommendations request failed", error);

      if (!this.#isCurrent(generation)) {
        return;
      }

      delete cache[productId];
      this.#hideSection();
    }
  }

  #applyRecommendations(nextRoot) {
    const nextWrapper = nextRoot.querySelector(CART_REC_SLIDER.wrapper);
    const currentWrapper = this.querySelector(CART_REC_SLIDER.wrapper);
    const canPatch = Boolean(
      nextWrapper &&
      currentWrapper &&
      this.querySelector(CART_REC_SLIDER.sticky) &&
      nextRoot.querySelector(CART_REC_SLIDER.sticky)
    );

    this.#clearAtcObservers();
    this.#removeRelocatedHosts();

    if (canPatch) {
      currentWrapper.replaceChildren(...nextWrapper.children);
      this.#mountAtcLoaders();
      this.#relocateQuickBuyHosts();
      this.#setStickyLoading(false);
      this.querySelector("cart-recommendations-slider")?.sync?.();
      return;
    }

    this.innerHTML = nextRoot.innerHTML;
    this.#mountAtcLoaders();
    this.#relocateQuickBuyHosts();
    this.#restoreAccordionState();
    this.#bindAccordionClose();
    this.#setStickyLoading(false);
  }

  #setStickyLoading(visible) {
    const sticky = this.querySelector(CART_REC_SLIDER.sticky);
    const toggle = sticky?.querySelector(CART_REC_SLIDER.stickyToggle);
    const plus = sticky?.querySelector(CART_REC_SLIDER.stickyPlus);
    const loader = sticky?.querySelector(CART_REC_SLIDER.stickyLoader);

    if (!sticky) {
      return;
    }

    sticky.classList.toggle("mini-cart__rec-slider-sticky--loading", visible);

    if (toggle) {
      if (visible) {
        toggle.setAttribute("aria-busy", "true");
      } else {
        toggle.removeAttribute("aria-busy");
      }
    }

    if (plus) {
      plus.hidden = visible;
    }

    if (loader) {
      loader.hidden = !visible;
    }
  }

  #mountAtcLoaders() {
    const prototype = ATC_LOADER_TEMPLATE.content.firstElementChild;

    if (!prototype) {
      return;
    }

    for (const imageWrapper of this.querySelectorAll(CART_REC_SLIDER.imageWrapper)) {
      if (imageWrapper.querySelector(CART_REC_SLIDER.atcLoader)) {
        continue;
      }

      imageWrapper.prepend(prototype.cloneNode(true));
    }
  }

  #onProductSubmit = (event) => {
    if (event.target.getAttribute("is") !== "product-form") {
      return;
    }

    const item = event.target.closest("product-item");

    if (!item || !this.contains(item)) {
      return;
    }

    this.#setAtcLoading(item, true);
    this.#watchAtcBusyState(item, event.target);
  };

  #onVariantAdded = (event) => {
    const item = event.target.closest("product-item");

    if (!item || !this.contains(item)) {
      return;
    }

    const state = this.#atcState.get(item) || {};
    state.succeeded = true;
    state.observer?.disconnect();
    this.#atcObservers.delete(state.observer);
    this.#atcState.set(item, state);
    this.#setAtcSuccess(item);
  };

  #watchAtcBusyState(item, form) {
    const button = form.querySelector('[type="submit"]');

    if (!(button instanceof HTMLElement)) {
      return;
    }

    const previous = this.#atcState.get(item);
    previous?.observer?.disconnect();
    this.#atcObservers.delete(previous?.observer);

    const observer = new MutationObserver(() => {
      if (button.getAttribute("aria-busy") === "true") {
        return;
      }

      observer.disconnect();
      this.#atcObservers.delete(observer);

      const state = this.#atcState.get(item);

      if (!state?.succeeded) {
        this.#setAtcLoading(item, false);
      }
    });

    observer.observe(button, {
      attributes: true,
      attributeFilter: ["aria-busy"]
    });

    this.#atcObservers.add(observer);
    this.#atcState.set(item, { succeeded: false, observer });
  }

  #setAtcLoading(item, visible) {
    const loader = item.querySelector(CART_REC_SLIDER.atcLoader);
    const spinner = loader?.querySelector(".product-item__atc-loader-spinner");
    const mark = loader?.querySelector(".product-item__atc-loader-mark");

    if (!loader) {
      return;
    }

    if (mark) {
      mark.hidden = true;
    }

    if (spinner) {
      spinner.hidden = !visible;
    }

    loader.hidden = !visible;
    item.classList.toggle("product-item--atc-loading", visible);
  }

  #setAtcSuccess(item) {
    const loader = item.querySelector(CART_REC_SLIDER.atcLoader);
    const spinner = loader?.querySelector(".product-item__atc-loader-spinner");
    const mark = loader?.querySelector(".product-item__atc-loader-mark");

    if (!loader) {
      return;
    }

    if (spinner) {
      spinner.hidden = true;
    }

    if (mark) {
      mark.hidden = false;
    }

    loader.hidden = false;
    item.classList.add("product-item--atc-loading");
  }

  #clearAtcObservers() {
    for (const observer of this.#atcObservers) {
      observer.disconnect();
    }

    this.#atcObservers.clear();
  }

  #storeAccordionState() {
    const panel = this.#accordionPanel;

    if (panel) {
      CartRecommendationsSliderInner.restoreAccordion = Boolean(panel.open);
    }
  }

  #restoreAccordionState() {
    if (!CartRecommendationsSliderInner.restoreAccordion) {
      return;
    }

    const panel = this.#accordionPanel;

    if (panel) {
      panel.open = true;
    }
  }

  #collapseAccordion() {
    const panel = this.#accordionPanel;

    if (!panel?.open) {
      return;
    }

    panel.open = false;
    CartRecommendationsSliderInner.restoreAccordion = false;
  }

  #getQuickBuyContainer() {
    const drawer = this.closest("cart-drawer");

    if (!drawer) {
      return null;
    }

    let container = drawer.querySelector(CART_REC_SLIDER.leftoverHosts);

    if (!container) {
      container = document.createElement("div");
      container.className = "cart-recommendations-quick-buy-container";
      drawer.append(container);
    }

    return container;
  }

  #removeRelocatedHosts() {
    const container = this.closest("cart-drawer")?.querySelector(CART_REC_SLIDER.leftoverHosts);

    if (!container) {
      this.#relocatedHostIds.clear();
      return;
    }

    for (const id of this.#relocatedHostIds) {
      const host = container.querySelector(`#${CSS.escape(id)}`);

      if (!host || host.hasAttribute("open")) {
        continue;
      }

      host.remove();
    }

    this.#relocatedHostIds.clear();

    if (!container.childElementCount) {
      container.remove();
    }
  }

  #relocateQuickBuyHosts() {
    const slider = this.querySelector("cart-recommendations-slider");
    const container = this.#getQuickBuyContainer();

    if (!slider || !container) {
      return;
    }

    for (const host of [...slider.querySelectorAll(CART_REC_SLIDER.quickBuyHost)]) {
      if (host.id) {
        const existing = container.querySelector(`#${CSS.escape(host.id)}`);

        if (existing?.hasAttribute("open")) {
          host.remove();
          this.#relocatedHostIds.add(host.id);
          continue;
        }

        existing?.remove();
        this.#relocatedHostIds.add(host.id);
      }

      container.append(host);
    }
  }

  #bindAccordionClose() {
    if (this.#accordionAbortController || !this.#accordionPanel) {
      return;
    }

    const drawer = this.closest("cart-drawer");
    const contentBox = drawer?.querySelector(".drawer__content");

    if (!drawer || !contentBox || !drawer.querySelector(CART_REC_SLIDER.cartItems)) {
      return;
    }

    this.#accordionAbortController = new AbortController();
    const { signal } = this.#accordionAbortController;
    this.#suppressAccordionCloseUntil = 0;
    this.#pointerInCartItems = false;

    listen(drawer, "pointerdown", this.#onDrawerPointerDown, { capture: true, signal });
    listen(contentBox, "wheel", this.#onCartItemsWheel, { capture: true, passive: true, signal });
    listen(contentBox, "scroll", this.#onCartItemsScroll, { passive: true, signal });
  }

  #unbindAccordionClose() {
    this.#accordionAbortController?.abort();
    this.#accordionAbortController = null;
  }

  #onDrawerPointerDown = (event) => {
    if (event.target.closest(CART_REC_SLIDER.quickBuyHost)) {
      return;
    }

    this.#pointerInCartItems = Boolean(event.target.closest(CART_REC_SLIDER.cartItems));

    const sectionRoot = this.#sectionRoot;

    if (sectionRoot && !sectionRoot.contains(event.target)) {
      this.#collapseAccordion();
    }
  };

  #onCartItemsWheel = (event) => {
    if (event.target.closest(CART_REC_SLIDER.cartItems)) {
      this.#collapseAccordion();
    }
  };

  #onCartItemsScroll = () => {
    if (performance.now() < this.#suppressAccordionCloseUntil || !this.#pointerInCartItems) {
      return;
    }

    this.#collapseAccordion();
  };
}

class CartRecommendationsSlider extends HTMLElement {
  #swiper = null;
  #collapsibleParent = null;
  #syncTimeoutId = 0;
  #abortController = null;

  sync() {
    this.#sync();
  }

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#collapsibleParent = this.closest("collapsible-content");

    if (this.#collapsibleParent) {
      listen(this.#collapsibleParent, "openable-element:open", this.#onCollapsibleOpen, {
        signal: this.#abortController.signal
      });
    }

    this.#init();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#collapsibleParent = null;
    this.#clearSyncTimeout();
    this.#destroy();
  }

  #onCollapsibleOpen = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#sync());
    });

    this.#clearSyncTimeout();
    this.#syncTimeoutId = window.setTimeout(() => {
      this.#syncTimeoutId = 0;
      this.#sync();
    }, SWIPER_SYNC_DELAY_MS);
  };

  #clearSyncTimeout() {
    if (!this.#syncTimeoutId) {
      return;
    }

    window.clearTimeout(this.#syncTimeoutId);
    this.#syncTimeoutId = 0;
  }

  #sync() {
    if (this.#swiper) {
      this.#swiper.update();
      return;
    }

    this.#init();
  }

  #init() {
    if (this.#swiper || typeof Swiper === "undefined") {
      return;
    }

    const collapsibleAncestor = this.closest("collapsible-content");

    if (collapsibleAncestor && !collapsibleAncestor.open) {
      return;
    }

    const sliderElement = this.querySelector(CART_REC_SLIDER.swiper);
    const wrapper = sliderElement?.querySelector(".swiper-wrapper");

    if (!wrapper?.querySelector(CART_REC_SLIDER.slide)) {
      return;
    }

    if (sliderElement.swiper) {
      this.#swiper = sliderElement.swiper;
      this.#swiper.update();
      return;
    }

    try {
      this.#swiper = new Swiper(sliderElement, SWIPER_PARAMS);
    } catch (error) {
      console.warn("CartRecommendationsSlider: Failed to initialize Swiper", error);
    }
  }

  #destroy() {
    if (!this.#swiper) {
      return;
    }

    this.#swiper.destroy(true, true);
    this.#swiper = null;
  }
}

defineOnce("cart-recommendations-slider-inner", CartRecommendationsSliderInner);
defineOnce("cart-recommendations-slider", CartRecommendationsSlider);